/* Copyright (c) 2024 Xperi. All Rights Reserved. */

import { MessageService } from '../../device';
import { Log } from '../../logging';
import { Storage } from '../../storage';
import { CcbSwitchName, CcbSwitchPartner, CcbSwitchVersion } from './enums';
import { ICcbSwitch } from './ICcbSwitch';

export class CcbSwitchImpl implements ICcbSwitch {
    private static readonly TAG: string = 'CcbSwitchImpl';

    /**
     * @inheritdoc
     */
    public mPartner: CcbSwitchPartner;

    /**
     * @inheritdoc
     */
    public mVersion: CcbSwitchVersion;

    // Define a mapping from partner names to unique set of switches.  Array index
    // is the enum index for CcbSwitchPartner.
    // Only one partner is active at a time.
    private readonly mSwitchMap: Map<CcbSwitchName, CcbSwitchVersion>;

    private readonly mOverriddenSwitchMap: Map<CcbSwitchName, CcbSwitchVersion>;

    public constructor() {
        this.mPartner = CcbSwitchPartner.UNIT_TEST;
        this.mVersion = CcbSwitchVersion.CCBAPPROVED;

        this.mOverriddenSwitchMap = new Map<CcbSwitchName, CcbSwitchVersion>();
        this.mSwitchMap = new Map<CcbSwitchName, CcbSwitchVersion>();

        this.initSwitchMap();
    }

    private initSwitchMap(): void {
        /**
         * Once a feature reaches CCBAPPROVED for ALL partners, the coding policy
         * states that the switch is to be *removed*, including the enum value
         * and all references that use CcbSwitch to look up the value.
         * While cleanup is pending, however, the feature might need to be
         * turned on quickly, so allow status CCBAPPROVED.  If not removing the code,
         * create a story to clean up the code!
         */
        /**
         * IF YOU ADD A NEW SWITCH HERE, YOU MUST ALSO UPDATE ALL SUBCLASSES OF CcbSwitchImpl
         */
        this.mSwitchMap.set(CcbSwitchName.ENABLE_DEVELOPER_SANDBOX, CcbSwitchVersion.DEVELOPER);
        this.mSwitchMap.set(CcbSwitchName.ENABLE_INCLUDE_ADULT, CcbSwitchVersion.NOTIMPLEMENTED);
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_SUBSCRIBED_CHANNELS,
            CcbSwitchVersion.NOTIMPLEMENTED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_CAST_SLT_REMOVE_IN_KST_12418,
            CcbSwitchVersion.DEVELOPER,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_MAY_ALSO_LIKE_SLT_REMOVE_IN_KST_12418,
            CcbSwitchVersion.DEVELOPER,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_PLAY_NEXT_REMOVE_IN_KST_12934,
            CcbSwitchVersion.DEVELOPER,
        );

        /**
         * Overridden CCB Switches
         */
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_BROWSE_MAIN_SCREEN_REMOVE_IN_KST_12159,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_AUDIO_AND_CC_SUBTITLE_PERSISTANCE_REMOVE_IN_KST_12164,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_SLT_REMOVE_IN_KST_12207,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_MY_RENTALS_REMOVE_IN_KST_12205,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_PREVIEW_REMOVE_IN_KST_14762,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_GALLERY_SCREEN_KST_12423,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_V2_CHANNELS_REMOVE_IN_KST_12928,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_PLAYBACK_REMOVE_IN_KST_12166,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_BROWSE_PROGRAM_SCREEN_REMOVE_IN_KST_12208,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_MOBI_DRM_REMOVE_IN_KST_7121,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_TRANSPORT_STREAM_FE_REMOVE_IN_KST_14322,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_ONDEMAND_EPISODE_REMOVE_IN_KST_12206,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_PLAY_ACTION_REMOVE_IN_KST_12288,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_INFO_CARD_REMOVE_IN_KST_12193,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_SIMPLIFIED_SIGN_IN_REMOVE_IN_KST_12173,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_DEVICE_REGISTRATION_REMOVE_IN_KST_14933,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mOverriddenSwitchMap.set(
            CcbSwitchName.ENABLE_V3_QUICK_MENU_SHORTCUT_REMOVE_IN_KST_16510,
            CcbSwitchVersion.CCBAPPROVED,
        );

        /**
         * Approved CCB Switches
         */
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_BROWSE_MAIN_SCREEN_REMOVE_IN_KST_12159,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_AUDIO_AND_CC_SUBTITLE_PERSISTANCE_REMOVE_IN_KST_12164,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_SLT_REMOVE_IN_KST_12207,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_MY_RENTALS_REMOVE_IN_KST_12205,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_PREVIEW_REMOVE_IN_KST_14762,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_GALLERY_SCREEN_KST_12423,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_V2_CHANNELS_REMOVE_IN_KST_12928,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_PLAYBACK_REMOVE_IN_KST_12166,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_BROWSE_PROGRAM_SCREEN_REMOVE_IN_KST_12208,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_MOBI_DRM_REMOVE_IN_KST_7121,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_TRANSPORT_STREAM_FE_REMOVE_IN_KST_14322,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_JUMP_CHANNEL_REMOVE_IN_KST_12189,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_ONDEMAND_EPISODE_REMOVE_IN_KST_12206,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_PLAY_ACTION_REMOVE_IN_KST_12288,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_INFO_CARD_REMOVE_IN_KST_12193,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_SIMPLIFIED_SIGN_IN_REMOVE_IN_KST_12173,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_VOD_DEVICE_REGISTRATION_REMOVE_IN_KST_14933,
            CcbSwitchVersion.CCBAPPROVED,
        );
        this.mSwitchMap.set(
            CcbSwitchName.ENABLE_V3_QUICK_MENU_SHORTCUT_REMOVE_IN_KST_16510,
            CcbSwitchVersion.CCBAPPROVED,
        );
    }

    // eslint-disable-next-line class-methods-use-this
    private getCcbSwitchNameEnumValue(enumName: string): CcbSwitchName {
        return CcbSwitchName[enumName as keyof typeof CcbSwitchName];
    }

    private async readCcbSwitchPartner() {
        await new MessageService(MessageService.CCB_SWITCH_PARTNER)
            .getResponse()
            .then((storedCcbPartner: any) => {
                if (storedCcbPartner === true) {
                    throw new Error('CcbSwitchPartner not found.');
                }

                this.mPartner = storedCcbPartner;
            })
            .catch((error) => {
                Log.e(CcbSwitchImpl.TAG, 'Failed to get ccb partner from host.', error);
                this.mPartner =
                    Storage.getInstance().getItem(Storage.Keys.CCB_SWITCH_PARTNER) ||
                    CcbSwitchPartner.UNIT_TEST;
            });
    }

    private async readCcbSwitchVersion() {
        await new MessageService(MessageService.CCB_SWITCH_VERSION)
            .getResponse()
            .then((storedCcbVersion: any) => {
                if (storedCcbVersion === null || !Object.keys(storedCcbVersion).length) {
                    throw new Error('CcbSwitchVersion not found.');
                }

                this.mVersion = Number(storedCcbVersion);
            })
            .catch((error) => {
                Log.e(CcbSwitchImpl.TAG, 'Failed to get ccb version from host.', error);

                this.mVersion =
                    Storage.getInstance().getItem(Storage.Keys.CCB_SWITCH_VERSION) ||
                    CcbSwitchVersion.CCBAPPROVED;
            });
    }

    private async readOverriddenSwitches() {
        await new MessageService(MessageService.CCB_OVERRIDDEN_SWITCHES)
            .getResponse()
            .then((storedSwitches: any) => {
                if (storedSwitches === true) {
                    throw new Error('CcbSwitchNames not found.');
                }

                storedSwitches.forEach((name) => {
                    const ccbSwitchNameEnumValue = this.getCcbSwitchNameEnumValue(name);
                    const version =
                        this.mSwitchMap.get(ccbSwitchNameEnumValue) ||
                        CcbSwitchVersion.NOTIMPLEMENTED;
                    this.mOverriddenSwitchMap.set(ccbSwitchNameEnumValue, version);
                });
            })
            .catch((error) => {
                Log.e(CcbSwitchImpl.TAG, 'Failed to get overridden switches from host.', error);
                const cdnStoredSwitches =
                    Storage.getInstance().getItem(Storage.Keys.CCB_OVERRIDDEN_SWITCHES) || [];
                cdnStoredSwitches.forEach((name) => {
                    const ccbSwitchNameEnumValue = this.getCcbSwitchNameEnumValue(name);
                    const version =
                        this.mSwitchMap.get(ccbSwitchNameEnumValue) ||
                        CcbSwitchVersion.NOTIMPLEMENTED;
                    this.mOverriddenSwitchMap.set(ccbSwitchNameEnumValue, version);
                });
            });
    }

    public async initialize() {
        await this.readCcbSwitchPartner();
        await this.readCcbSwitchVersion();
        await this.readOverriddenSwitches();
    }

    public isEnabledAndOverriden(ccbSwitch: CcbSwitchName): boolean {
        return this.isEnabled(ccbSwitch) && this.mOverriddenSwitchMap.has(ccbSwitch);
    }

    /**
     * @inheritdoc
     */
    public getValue(ccbSwitch: CcbSwitchName): CcbSwitchVersion {
        return this.mSwitchMap.get(ccbSwitch) || CcbSwitchVersion.CCBAPPROVED;
    }

    /**
     * @inheritdoc
     */
    public isOverridden(ccbSwitch: CcbSwitchName): boolean {
        return this.mOverriddenSwitchMap.has(ccbSwitch);
    }

    /**
     * @inheritdoc
     */
    public isEnabled(ccbSwitch: CcbSwitchName): boolean {
        // Once a feature reaches CCBAPPROVED, the coding policy states that
        // the switch is to be *removed*, including the enum value and all
        // references that use CcbSwitch to look up the value.
        // While cleanup is pending, however, the feature might need to be
        // turned on quickly, so allow status CCBAPPROVED. If the enum value still
        // exists, but isn't in the map at all, take this is an indication
        // of approval.
        const versionForFeature: CcbSwitchVersion =
            this.mSwitchMap.get(ccbSwitch) || CcbSwitchVersion.CCBAPPROVED;

        switch (versionForFeature) {
            case CcbSwitchVersion.CCBAPPROVED:
                return true;

            case CcbSwitchVersion.DEVELOPER:
                return (
                    this.mVersion === CcbSwitchVersion.DEVELOPER ||
                    this.mVersion === CcbSwitchVersion.TEST
                );

            case CcbSwitchVersion.NOTIMPLEMENTED:
                // The feature has not been started, so the enum value just
                // represents future work at this point.
                return false;

            case CcbSwitchVersion.QEREADY:
                return (
                    this.mVersion === CcbSwitchVersion.QEREADY ||
                    this.mVersion === CcbSwitchVersion.DEVELOPER ||
                    this.mVersion === CcbSwitchVersion.TEST
                );

            case CcbSwitchVersion.TAKEHOME:
                return (
                    this.mVersion === CcbSwitchVersion.TAKEHOME ||
                    this.mVersion === CcbSwitchVersion.QEREADY ||
                    this.mVersion === CcbSwitchVersion.DEVELOPER ||
                    this.mVersion === CcbSwitchVersion.TEST
                );

            case CcbSwitchVersion.TEST:
                // TEST is only used for the runtime version, not for feature enablement
                if (this.mVersion === CcbSwitchVersion.TEST) {
                    Log.w(
                        CcbSwitchImpl.TAG,
                        'TEST value used outside unit tests. Override initflags',
                    );
                }
                return this.mVersion === CcbSwitchVersion.TEST;

            default:
        }
        return false;
    }

    /**
     * @inheritdoc
     */
    public overrideValue(ccbSwitch: CcbSwitchName, version: CcbSwitchVersion): void {
        this.mOverriddenSwitchMap.set(ccbSwitch, version);
    }
}
